import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Dropdown from 'react-bootstrap/Dropdown';
import { useAppConfig } from '@/api/useAppConfig';
import { SUPPORTED_CULTURES } from '@/i18n';
import { baseLanguage, flagClass, languageName } from '@/i18n/languages';
import { useChangeLanguage } from '@/i18n/useChangeLanguage';
import { useLayout } from '@/theme/useLayout';

/**
 * Layout for the pages that have no session yet — the React equivalent of
 * `_AuthLayout.cshtml`: a centred card on a gradient, with a theme toggle and a
 * language picker pinned to the corners.
 *
 * Its inline `<style>` block now lives in `styles/shell.scss`, scoped to
 * `.auth-layout` because the SPA is a single document and those rules targeted
 * `body`.
 *
 * The Razor language list was hardcoded to five entries — including de-DE, which
 * this application does not configure — while the header's list came from
 * `SupportedCultures`. Both read the configured list now, so the two switchers
 * cannot drift apart.
 */
export function AuthShell() {
  const { t, i18n } = useTranslation();
  const { data: config } = useAppConfig();
  const { colorMode, toggleColorMode } = useLayout();
  const changeLanguage = useChangeLanguage();

  const cultures = config?.supportedCultures?.length ? config.supportedCultures : SUPPORTED_CULTURES;
  const current = baseLanguage(i18n.language);

  return (
    <div className="auth-layout">
      <div className="theme-toggle-auth d-none d-md-block">
        <button
          type="button"
          className="btn btn-light shadow-sm"
          onClick={toggleColorMode}
          aria-label={t('ToggleDarkMode', 'Toggle dark mode')}
          aria-pressed={colorMode === 'dark'}
        >
          <svg className="sa-icon sa-mode-light" width="24" height="24">
            <use href="/myui/icons/sprite.svg#sun" />
          </svg>
          <svg className="sa-icon sa-mode-dark" width="24" height="24">
            <use href="/myui/icons/sprite.svg#moon" />
          </svg>
        </button>
      </div>

      <div className="language-selector-auth d-none d-md-block">
        <Dropdown>
          <Dropdown.Toggle
            variant="light"
            className="shadow-sm"
            title={languageName(i18n.language)}
          >
            <span className={`${flagClass(i18n.language)} me-2`} />
            <span className="d-none d-lg-inline">{current.toUpperCase()}</span>
          </Dropdown.Toggle>

          <Dropdown.Menu>
            {cultures.map((culture) => {
              const active = baseLanguage(culture) === current;
              return (
                <Dropdown.Item
                  key={culture}
                  as="button"
                  type="button"
                  active={active}
                  onClick={() => void changeLanguage(culture)}
                >
                  <span className={`${flagClass(culture)} me-2`} />
                  {languageName(culture)}
                  {active && <span className="ms-2">✓</span>}
                </Dropdown.Item>
              );
            })}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div className="auth-layout-container">
        <div className="container">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
